/**
 * Accordion Block JavaScript - BEM Implementation
 * Handles interactive accordion functionality with accessibility features
 */
document.addEventListener('DOMContentLoaded', function() {
    initAccordionBlocks();
});

function initAccordionBlocks() {
    const accordionBlocks = document.querySelectorAll('.accordion-block');
    
    accordionBlocks.forEach(initAccordionBlock);
}

function initAccordionBlock(blockElement) {
    const accordionItems = blockElement.querySelectorAll('.accordion-block__item');
    const behavior = blockElement.dataset.behavior || 'single';
    
    accordionItems.forEach((item, index) => {
        // Set CSS variable for staggered animations
        item.style.setProperty('--item-index', index);
        
        const header = item.querySelector('.accordion-block__header');
        const content = item.querySelector('.accordion-block__content');
        const body = item.querySelector('.accordion-block__body');
        
        if (header && content) {
            // Get initial state from data attribute
            const isOpen = content.dataset.open === 'true';
            
            // Set up initial ARIA attributes
            header.setAttribute('aria-expanded', isOpen.toString());
            header.setAttribute('role', 'button');
            header.setAttribute('tabindex', '0');
            
            // Add click event listener
            header.addEventListener('click', function() {
                toggleAccordionItem(item, header, content, blockElement, behavior);
            });
            
            // Add keyboard event listener
            header.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleAccordionItem(item, header, content, blockElement, behavior);
                } else {
                    handleKeyNavigation(e, blockElement, header);
                }
            });
            
            // Initialize state
            if (isOpen) {
                openAccordionItem(item, header, content);
            } else {
                closeAccordionItem(item, header, content);
            }
        }
    });
    
    // Add keyboard navigation between accordion items
    setupKeyboardNavigation(blockElement);
}

function toggleAccordionItem(item, header, content, blockElement, behavior) {
    const isOpen = content.dataset.open === 'true';
    
    // Handle single behavior - close other items
    if (behavior === 'single' && !isOpen) {
        const allItems = blockElement.querySelectorAll('.accordion-block__item');
        allItems.forEach(otherItem => {
            if (otherItem !== item) {
                const otherHeader = otherItem.querySelector('.accordion-block__header');
                const otherContent = otherItem.querySelector('.accordion-block__content');
                if (otherHeader && otherContent) {
                    closeAccordionItem(otherItem, otherHeader, otherContent);
                }
            }
        });
    }
    
    if (isOpen) {
        closeAccordionItem(item, header, content);
    } else {
        openAccordionItem(item, header, content);
    }
}

function openAccordionItem(item, header, content) {
    // Update data attributes and ARIA
    content.dataset.open = 'true';
    item.dataset.collapsed = 'false';
    header.setAttribute('aria-expanded', 'true');
    
    // Calculate actual height for smooth animation
    const body = content.querySelector('.accordion-block__body');
    if (body) {
        const height = body.scrollHeight;
        content.style.maxHeight = height + 'px';
    }
    
    // Emit custom event
    item.dispatchEvent(new CustomEvent('accordionItemOpened', {
        bubbles: true,
        detail: { item, header, content }
    }));
}

function closeAccordionItem(item, header, content) {
    // Update data attributes and ARIA
    content.dataset.open = 'false';
    item.dataset.collapsed = 'true';
    header.setAttribute('aria-expanded', 'false');
    content.style.maxHeight = '0px';
    
    // Emit custom event
    item.dispatchEvent(new CustomEvent('accordionItemClosed', {
        bubbles: true,
        detail: { item, header, content }
    }));
}

function handleKeyNavigation(e, blockElement, currentHeader) {
    const accordionHeaders = Array.from(blockElement.querySelectorAll('.accordion-block__header'));
    const currentIndex = accordionHeaders.indexOf(currentHeader);
    
    switch (e.key) {
        case 'ArrowDown':
            e.preventDefault();
            const nextIndex = (currentIndex + 1) % accordionHeaders.length;
            accordionHeaders[nextIndex].focus();
            break;
            
        case 'ArrowUp':
            e.preventDefault();
            const prevIndex = currentIndex === 0 ? accordionHeaders.length - 1 : currentIndex - 1;
            accordionHeaders[prevIndex].focus();
            break;
            
        case 'Home':
            e.preventDefault();
            accordionHeaders[0].focus();
            break;
            
        case 'End':
            e.preventDefault();
            accordionHeaders[accordionHeaders.length - 1].focus();
            break;
    }
}

function setupKeyboardNavigation(blockElement) {
    const accordionHeaders = blockElement.querySelectorAll('.accordion-block__header');
    
    // Set tabindex for proper keyboard navigation
    accordionHeaders.forEach((header, index) => {
        header.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
    
    // Handle focus management
    blockElement.addEventListener('focusin', function(e) {
        if (e.target.classList.contains('accordion-block__header')) {
            accordionHeaders.forEach(header => header.setAttribute('tabindex', '-1'));
            e.target.setAttribute('tabindex', '0');
        }
    });
}

function generateUniqueId(prefix) {
    return prefix + '-' + Math.random().toString(36).substr(2, 9);
}

// Utility functions for external control
function openAccordionItemById(blockElement, itemIndex) {
    const items = blockElement.querySelectorAll('.accordion-block__item');
    const item = items[itemIndex];
    
    if (item) {
        const header = item.querySelector('.accordion-block__header');
        const content = item.querySelector('.accordion-block__content');
        
        if (header && content) {
            openAccordionItem(item, header, content);
        }
    }
}

function closeAccordionItemById(blockElement, itemIndex) {
    const items = blockElement.querySelectorAll('.accordion-block__item');
    const item = items[itemIndex];
    
    if (item) {
        const header = item.querySelector('.accordion-block__header');
        const content = item.querySelector('.accordion-block__content');
        
        if (header && content) {
            closeAccordionItem(item, header, content);
        }
    }
}

function closeAllAccordionItems(blockElement) {
    const items = blockElement.querySelectorAll('.accordion-block__item');
    
    items.forEach(item => {
        const header = item.querySelector('.accordion-block__header');
        const content = item.querySelector('.accordion-block__content');
        
        if (header && content) {
            closeAccordionItem(item, header, content);
        }
    });
}

// Handle window resize to recalculate content heights
let resizeTimer;
window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
        const openContents = document.querySelectorAll('.accordion-block__content[data-open="true"]');
        
        openContents.forEach(content => {
            const body = content.querySelector('.accordion-block__body');
            if (body) {
                const height = body.scrollHeight;
                content.style.maxHeight = height + 'px';
            }
        });
    }, 250);
});

// Export functions for potential external use
window.AccordionBlock = {
    init: initAccordionBlocks,
    initSingle: initAccordionBlock,
    openItem: openAccordionItemById,
    closeItem: closeAccordionItemById,
    closeAll: closeAllAccordionItems
};

// Auto-initialize when new accordion blocks are added to DOM
if (window.MutationObserver) {
    const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
            mutation.addedNodes.forEach(function(node) {
                if (node.nodeType === 1) { // Element node
                    const accordionBlocks = node.querySelectorAll ? node.querySelectorAll('.accordion-block') : [];
                    
                    if (node.classList && node.classList.contains('accordion-block')) {
                        initAccordionBlock(node);
                    } else {
                        accordionBlocks.forEach(initAccordionBlock);
                    }
                }
            });
        });
    });
    
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
}